/**
 * Pixel Streaming Progress Service
 * 
 * Centralized service to manage pixel streaming progress via Server-Sent Events (SSE).
 * Uses SSE instead of polling - backend pushes updates when POST requests arrive.
 * Prevents duplicate connections for the same project by multiple components.
 */

import { baseApi } from '../API/api';

class PixelStreamingProgressService {
  constructor() {
    // Map of projectId -> { subscribers, eventSource, lastProgress }
    this.streamingState = new Map();
  }

  /**
   * Subscribe to progress updates for a project via SSE
   * @param {string} projectId - Project ID
   * @param {Function} callback - Callback function (progress, status, ready)
   * @returns {Function} Unsubscribe function
   */
  subscribe(projectId, callback) {
    if (!projectId || !callback) {
      console.warn('PixelStreamingProgressService: Invalid subscribe parameters');
      return () => {};
    }

    const state = this.streamingState.get(projectId) || {
      subscribers: new Set(),
      eventSource: null,
      lastProgress: null
    };

    state.subscribers.add(callback);
    this.streamingState.set(projectId, state);

    // Start SSE stream if this is the first subscriber
    if (state.subscribers.size === 1) {
      this.startStreaming(projectId);
    }

    // Return unsubscribe function
    return () => {
      this.unsubscribe(projectId, callback);
    };
  }

  /**
   * Unsubscribe from progress updates
   * @param {string} projectId - Project ID
   * @param {Function} callback - Callback function to remove
   */
  unsubscribe(projectId, callback) {
    const state = this.streamingState.get(projectId);
    if (!state) return;

    state.subscribers.delete(callback);

    // Stop streaming if no more subscribers
    if (state.subscribers.size === 0) {
      this.stopStreaming(projectId);
      this.streamingState.delete(projectId);
    }
  }

  /**
   * Start SSE streaming for a project
   * @param {string} projectId - Project ID
   */
  startStreaming(projectId) {
    const state = this.streamingState.get(projectId);
    if (!state || state.eventSource) {
      return; // Already streaming
    }

    // Create EventSource for SSE
    const eventSource = new EventSource(`${baseApi}/api/pixelstreaming/ready/${projectId}/stream`);
    state.eventSource = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.error) {
          console.error(`🎬 ❌ SSE error for project ${projectId}:`, data.error);
          // Notify subscribers of error
          state.subscribers.forEach(callback => {
            try {
              callback(null, 'error', false);
            } catch (err) {
              console.error('Error in error callback:', err);
            }
          });
          return;
        }

        // Clamp progress between 0-100%
        const rawProgress = data.progress || 0;
        const progress = Math.max(0, Math.min(100, rawProgress));
        const status = data.status;
        // Ensure ready is explicitly false if progress < 100, even if backend sends ready=true
        // This prevents stale ready=true from triggering premature navigation
        const ready = (progress >= 100 && data.ready === true) ? true : false;

        state.lastProgress = progress;

        // Notify all subscribers
        state.subscribers.forEach(callback => {
          try {
            callback(progress, status, ready);
          } catch (error) {
            console.error('Error in progress callback:', error);
          }
        });

        // Close stream if progress is complete
        if (progress >= 100 && ready) {
          console.log(`🎬 ✅ SSE stream completed for project ${projectId} (100%)`);
          this.stopStreaming(projectId);
        }
      } catch (error) {
        console.error(`🎬 ❌ Error parsing SSE data for project ${projectId}:`, error);
      }
    };

    eventSource.onerror = (error) => {
      console.error(`🎬 ❌ SSE connection error for project ${projectId}:`, error);
      console.error(`🎬 ❌ EventSource readyState: ${eventSource.readyState} (0=CONNECTING, 1=OPEN, 2=CLOSED)`);
      console.error(`🎬 ❌ EventSource URL: ${eventSource.url}`);
      
      // Only notify subscribers of error if connection is actually closed
      // EventSource will auto-reconnect on transient errors (readyState = CONNECTING)
      // We should only show error if it's permanently closed (readyState = CLOSED)
      if (eventSource.readyState === EventSource.CLOSED) {
        console.error(`🎬 ❌ SSE connection closed for project ${projectId}. Will attempt to reconnect...`);
        
        // Only notify subscribers if connection is permanently closed
        // Don't notify on transient connection issues that will auto-reconnect
        state.subscribers.forEach(callback => {
          try {
            callback(null, 'error', false);
          } catch (err) {
            console.error('Error in error callback:', err);
          }
        });
      } else {
        // Transient error - EventSource is reconnecting (readyState = CONNECTING)
        // Don't show error message, just log it
        console.log(`🎬 ⚠️ SSE transient connection issue for project ${projectId}, auto-reconnecting...`);
      }
    };

    console.log(`🎬 📡 SSE stream started for project ${projectId}`);
  }

  /**
   * Stop SSE streaming for a project
   * @param {string} projectId - Project ID
   */
  stopStreaming(projectId) {
    const state = this.streamingState.get(projectId);
    if (!state) return;

    if (state.eventSource) {
      state.eventSource.close();
      state.eventSource = null;
      console.log(`🎬 📡 SSE stream stopped for project ${projectId}`);
    }
  }

  /**
   * Get current progress for a project (if available)
   * @param {string} projectId - Project ID
   * @returns {Object|null} Current progress data or null
   */
  getCurrentProgress(projectId) {
    const state = this.streamingState.get(projectId);
    if (!state || state.lastProgress === null) {
      return null;
    }
    return {
      progress: state.lastProgress
    };
  }
}

// Export singleton instance
const pixelStreamingProgressService = new PixelStreamingProgressService();
export default pixelStreamingProgressService;

